import type { IUpdateVoucherCmd } from "@/data/commands/table/CreateTableCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { VoucherDao } from "@/data/dao/service/VoucherDao";
import type { IVoucher } from "@/data/model/Voucher";
import { exceptionTypes } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";

import { NumberInput } from "@/portal/formControls/NumberInput";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import {i18n} from "@/portal/Trans";
import {logError} from "@/shared/logging/errors";
import {UiNotification} from "@/portal/ui/Notification";

export class VoucherForm extends BaseEntityForm<IVoucher, IUpdateVoucherCmd> {
    private model: IVoucher | undefined;
    private $createdDateTimeField: JQuery;
    private $expirationDateTimeField: JQuery;
    private $cancellationDateTimeField: JQuery;
    private initialValueField: NumberInput;
    private balanceField: NumberInput;
    private $voucherCodeField: JQuery;
    private $internalNote: JQuery;
    private $cancelBtn: JQuery;
    
    constructor(element: JQuery) {
        super(element, Factory.getInstance(VoucherDao));

        this.$createdDateTimeField = this.$form.find('.form-control[name="created-date-time"]');
        this.$expirationDateTimeField = this.$form.find('.form-control[name="expiration-date-time"]');
        this.$cancellationDateTimeField = this.$form.find('.form-control[name="cancellation-date-time"]');
        this.$cancelBtn = $('#cancel-voucher');

        this.initialValueField = new NumberInput(this.$form.find('.form-control[name="initial-value"]'), {
            min: 0,
            step: 0.01,
            decimals: 2,
        });

        this.balanceField = new NumberInput(this.$form.find('.form-control[name="balance"]'), {
            min: 0,
            step: 0.01,
            decimals: 2,
        });

        this.$voucherCodeField = this.$form.find('.form-control[name="voucher-code"]');
        this.$internalNote = this.$form.find('.form-control[name="internal-note"]');
        
        this.$cancelBtn.on('click', () => {
            const dao = Factory.getInstance<VoucherDao>(VoucherDao);
            dao.cancel(this.id!!).then((voucher) => {
                this.setFormData(voucher);
                this.showSuccess(i18n("general.successfullyUpdated"));
            }).catch((err) => {
                logError(err);
                UiNotification.showException(i18n("general.failedUpdate"));
            })
        });
    }

    public setFormData(model?: IVoucher) {
        this.model = model;
        
        const isCancelled = model?.isCancelled === true;
        const issuedInPortal = model?.issuedInPortal === true;
        
        this.$saveBtn.prop("disabled", isCancelled);
        this.$internalNote.prop("disabled", isCancelled);
        this.$cancelBtn.prop("disabled", model == null || isCancelled || !issuedInPortal);
        
        this.$createdDateTimeField.val(
            model === undefined ? "" : DateTime.toPreciseDateTimeString(model.createdDateTime)
        );
        this.$createdDateTimeField.prop("disabled", true);

        this.$expirationDateTimeField.val(
            model === undefined ? "" : DateTime.toPreciseDateTimeString(model.expirationDateTime)
        );
        this.$expirationDateTimeField.prop("disabled", true);

        this.$cancellationDateTimeField.val(
            model === undefined || model.cancellationDateTime == null ? "" : DateTime.toPreciseDateTimeString(model.cancellationDateTime)
        );
        this.$cancellationDateTimeField.prop("disabled", true);

        this.initialValueField.setValue(model === undefined ? undefined : model.initialValue);
        this.initialValueField.setDisabled(true);

        this.balanceField.setValue(model === undefined ? undefined : model.balance);
        this.balanceField.setDisabled(true);

        this.$voucherCodeField.val(model === undefined ? "" : model.code);
        this.$voucherCodeField.prop("disabled", true);

        this.$internalNote.val(model === undefined ? "" : (model?.internalNote ?? ""));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): IUpdateVoucherCmd {
        let hasError = false;
        const data: IUpdateVoucherCmd = {
            internalNote: this.$internalNote.val(),
        };

        if (data.internalNote != null && data.internalNote.length > 25) {
            hasError = true;
            this.$internalNote?.addClass("is-invalid");
        } else {
            this.$internalNote?.removeClass("is-invalid");
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.voucherAlreadyUsedException:
                return i18n("voucher.alreadyUsedCannotBeDeleted");
        }
        return b.additionalData.reason;
    }
}
